import { inject, Injectable } from '@angular/core';
import {
    ApprovedPCHDurationModel,
    ArrayModel,
    CriticalityModel,
    DataMaintenanceLocationModel,
    DataMaintenanceSystemConfigModel,
    DopModel,
    EventService,
    IMaterialPickupResult,
    MaterialCheckModel,
    internalInfoModelToLongTextLines,
    MaterialIdentifierModel,
    MaterialModel,
    MaterialOrderModel,
    MaterialPickupComponent,
    MaterialQuoteModel,
    MaterialService,
    ModalClass,
    NotificationDurationModel,
    NotificationIdentifier,
    NotificationModel,
    selectCentricarePCHWarningTime,
    SynchronizationService,
    updateNotificationInternalInfoLines,
} from '@evorait/pace-core';
import { getDocBox } from '@evorait/pace-forms';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Store } from '@ngrx/store';
import { BsModalService } from 'ngx-bootstrap/modal';
import moment from 'moment';
import { of } from 'rxjs';
import { catchError, mergeMap, take, tap } from 'rxjs/operators';
import { PCHAlertService } from '../../core/services/pch-alerts/pch-alerts.service';
import { MaterialOrderComponent } from '../../material/material-order/material-order.component';
import { MaterialPricingComponent } from '../../material/material-pricing/material-pricing.component';
import { MaterialPricingModel } from '../../material/material-pricing/material-pricing.model';
import { MaterialQuoteComponent } from '../../material/material-quote/material-quote.component';
import { LocalStorageSalesQuoteService } from '../../material/services/local-storage-sales-quote.service';
import { ContractComponent } from '../contract/contract.component';
import { CriticalityComponent } from '../criticality/criticality.component';
import { CustomerComponent } from '../customer/customer.component';
import { DataMaintenanceLocationComponent } from '../data-maintenance/data-maintenance-location.component';
import { DataMaintenanceSysconfigComponent } from '../data-maintenance/data-maintenance-sysconfig.component';
import { DurationComponent } from '../duration/duration.component';
import { EquipmentConfigurationComponent } from '../equipment-configuration/equipment-configuration.component';
import EquipmentConfigurationModel from '../equipment-configuration/equipment-configuration.model';
import { EquipmentHistoryComponent } from '../equipment-history/equipment-history.component';
import EquipmentHistoryModel from '../equipment-history/equipment-history.model';
import { EquipmentOtherTasksComponent } from '../equipment-other-tasks/equipment-other-tasks.component';
import { InternalInfoComponent } from '../internal-info/internal-info.component';
import { ObjectInfoComponent } from '../object-info/object-info.component';
import { PCHAlertComponent } from '../pch-alert/pch-alert.component';
import { PCHDurationComponent } from '../pch-duration/pch-duration.component';

/**
 * Opens the dialogs of the notification-details screen.
 *
 * All of them take a `NotificationModel` or a model built from one, so this is where the store-built
 * model from `NotificationModelFactory` is handed over. Keeping them out of the screen leaves it to
 * do nothing but read the store and assemble view models, and confines the model-based collaborators
 * to one file.
 */
@Injectable({ providedIn: 'root' })
export class NotificationDetailsModalsService {
    private readonly log: LogInterface = inject(LogInstanceToken);
    private readonly store = inject(Store);
    private readonly modalService = inject(BsModalService);
    private readonly materialService = inject(MaterialService);
    private readonly localStorageSalesQuoteService = inject(LocalStorageSalesQuoteService);
    private readonly synchronizationService = inject(SynchronizationService);
    private readonly eventService = inject(EventService);
    private readonly pchAlertService = inject(PCHAlertService);

    private readonly centricarePCHWarningTime = this.store.selectSignal(selectCentricarePCHWarningTime);

    private isOpeningMaterialRequest = false;

    public openCustomer(notification: NotificationModel): void {
        this.modalService.show(CustomerComponent, {
            class: ModalClass.MODAL_MEDIUM,
            initialState: { notification },
            ariaLabelledBy: 'customer-modal-title',
        });
    }

    public openContract(notification: NotificationModel): void {
        this.modalService.show(ContractComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: { notification },
            ariaLabelledBy: 'contract-modal-title',
        });
    }

    public openObjectInfo(notification: NotificationModel): void {
        this.modalService.show(ObjectInfoComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: { notification },
            ariaLabelledBy: 'object-info-modal-title',
        });
    }

    public openCriticality(notification: NotificationModel): void {
        this.modalService.show(CriticalityComponent, {
            class: ModalClass.MODAL_MEDIUM,
            initialState: { model: new CriticalityModel(notification) },
            ariaLabelledBy: 'criticality-modal-title',
        });
    }

    /**
     * @param notification the notification whose duration is changed
     * @param durationTaskNumber task number of the notification's DURA task
     */
    public openDuration(notification: NotificationModel, durationTaskNumber: string): void {
        this.modalService.show(DurationComponent, {
            class: ModalClass.MODAL_MEDIUM,
            initialState: { model: new NotificationDurationModel(notification, durationTaskNumber) },
            ariaLabelledBy: 'duration-modal-title',
        });
    }

    /**
     * Saving the internal info has to reach the backend right away, which is why this one waits for
     * its result instead of firing and forgetting like the read-only dialogs above.
     *
     * @param notification the notification whose internal info is edited
     */
    public openInternalInfo(notification: NotificationModel): void {
        const modalInstance = this.modalService.show(InternalInfoComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_LARGE,
            initialState: { model: notification.internalInfoModel },
            ariaLabelledBy: 'internal-info-modal-title',
        });
        modalInstance.content?.modalResult
            .pipe(
                take(1),
                tap((result) => {
                    if (!result) {
                        return;
                    }
                    // The modal edited `notification.internalInfoModel` in place; write those edits back onto
                    // the notification's slice entry too, or a `NotificationModel` rebuilt from the store later
                    // (see `NotificationModelFactory.create()`) would silently lose them.
                    this.store.dispatch(
                        updateNotificationInternalInfoLines({
                            id: {
                                frontId: notification.identifier.frontId,
                                qmNum: notification.identifier.qmNum,
                                maNum: notification.identifier.maNum,
                            },
                            internalInfoLines: internalInfoModelToLongTextLines(notification.internalInfoModel),
                        }),
                    );
                }),
                mergeMap((result) => (result ? this.synchronizationService.sendOfflineData() : of(undefined))),
                catchError((error) => {
                    this.log.error(
                        'NotificationDetailsModalsService: openInternalInfo: Error while saving the Internal Info contact model:',
                        error,
                    );
                    return of(undefined);
                }),
            )
            .subscribe();
    }

    /**
     * The approved-PCH dialog. Submitting a new approved PCH re-schedules the PCH reminder, which is
     * why the caller's notification is needed beyond building the model.
     *
     * @param notification the notification whose approved PCH is edited
     */
    public openApprovedPchDuration(notification: NotificationModel): void {
        const model = new ApprovedPCHDurationModel(notification);
        this.modalService
            .show(PCHDurationComponent, {
                class: ModalClass.MODAL_MEDIUM,
                initialState: { model },
                ariaLabelledBy: 'pch-duration-modal-title',
            })
            .content?.triggerPCHAlertOnAPCHUpdate.pipe(take(1))
            .subscribe(() => {
                const triggerTime = model.getPCHAlertTriggerTimeOnAPCHUpdate(this.centricarePCHWarningTime());
                if (triggerTime) {
                    this.addPchReminder(triggerTime, notification.identifier);
                }
            });
    }

    /**
     * The PCH alert, whose only action is to open the approved-PCH dialog behind it.
     *
     * @param notification
     */
    public openPchAlert(notification: NotificationModel): void {
        this.modalService
            .show(PCHAlertComponent, {
                class: ModalClass.MODAL_MEDIUM,
                initialState: {},
                ariaLabelledBy: 'pch-alert-modal-title',
            })
            .content?.openPCHDurationModel.pipe(take(1))
            .subscribe(() => this.openApprovedPchDuration(notification));
    }

    /**
     * Schedules the PCH reminder for a notification that has just reached the on-site status.
     *
     * @param notification the notification the reminder belongs to
     */
    public schedulePchReminderForOnSite(notification: NotificationModel): void {
        const model = new ApprovedPCHDurationModel(notification);
        const triggerTime = model.getPCHAlertTriggerTime(this.centricarePCHWarningTime());
        if (triggerTime) {
            this.addPchReminder(triggerTime, notification.identifier);
        }
    }

    public openEquipmentHistory(notification: NotificationModel): void {
        this.modalService.show(EquipmentHistoryComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_LARGE,
            initialState: { model: new EquipmentHistoryModel(notification) },
            ariaLabelledBy: 'equipment-history-modal-title',
        });
    }

    public openEquipmentConfiguration(notification: NotificationModel): void {
        this.modalService.show(EquipmentConfigurationComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_LARGE,
            initialState: {
                model: new EquipmentConfigurationModel(notification),
                notification,
            },
            ariaLabelledBy: 'equipment-configuration-modal-title',
        });
    }

    public openEquipmentOtherTasks(notification: NotificationModel): void {
        this.modalService.show(EquipmentOtherTasksComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_LARGE,
            initialState: { notification },
        });
    }

    public openMaintenanceLocation(notification: NotificationModel): void {
        this.modalService.show(DataMaintenanceLocationComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: { model: new DataMaintenanceLocationModel(notification) },
            ariaLabelledBy: 'data-maintenance-location-modal-title',
        });
    }

    public openSystemConfiguration(notification: NotificationModel): void {
        this.modalService.show(DataMaintenanceSysconfigComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: { model: new DataMaintenanceSystemConfigModel(notification) },
            ariaLabelledBy: 'data-maintenance-system-config-modal-title',
        });
    }

    /**
     * The material request, which first reads back a previously saved but unsent order. The guard
     * against a second open is needed because that read is asynchronous.
     *
     * @param notification the notification the material is requested for
     * @param displayMaterialPricing whether the order dialog offers pricing
     * @param displayMaterialQuote whether the order dialog offers quotes
     */
    public openMaterialRequest(
        notification: NotificationModel,
        displayMaterialPricing: boolean,
        displayMaterialQuote: boolean,
    ): void {
        if (this.isOpeningMaterialRequest) {
            return;
        }
        this.isOpeningMaterialRequest = true;

        this.materialService
            .getSavedMaterialOrderModel(notification.identifier)
            .pipe(take(1))
            .subscribe({
                next: (savedMaterialOrderModel) => {
                    this.isOpeningMaterialRequest = false;
                    if (savedMaterialOrderModel?.isSubmitted) {
                        return;
                    }
                    this.modalService.show(MaterialOrderComponent, {
                        backdrop: 'static',
                        class: ModalClass.MODAL_LARGE,
                        initialState: {
                            model: new MaterialOrderModel(notification, undefined, savedMaterialOrderModel),
                            displayMaterialPricing,
                            displayMaterialQuote,
                        },
                        ariaLabelledBy: 'material-order-modal-title',
                    });
                },
                error: (error: unknown) => {
                    this.isOpeningMaterialRequest = false;
                    this.log.error(
                        'NotificationDetailsModalsService: openMaterialRequest: failed to read the saved material order',
                        error,
                    );
                },
            });
    }

    public openMaterialQuote(notification: NotificationModel): void {
        this.localStorageSalesQuoteService
            .getSalesQuoteAsync(notification.identifier)
            .then((localSalesQuote) => {
                this.modalService.show(MaterialQuoteComponent, {
                    backdrop: 'static',
                    class: ModalClass.MODAL_LARGE,
                    initialState: {
                        model: localSalesQuote ?? new MaterialQuoteModel(undefined, notification),
                    },
                    ariaLabelledBy: 'material-quote-modal-title',
                });
            })
            .catch((error) => {
                this.log.error(this.constructor.name, 'failed to read and show sales quote', error);
            });
    }

    public openMaterialPricing(notification: NotificationModel, displayMaterialQuote: boolean): void {
        const materialPricingModal = this.modalService.show(MaterialPricingComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_LARGE,
            initialState: {
                model: new MaterialPricingModel(notification),
                displayMaterialQuote,
            },
            ariaLabelledBy: 'material-pricing-modal-title',
        });

        materialPricingModal.content?.materialsConfirmed.subscribe((materials: MaterialCheckModel[]) => {
            this.modalService.show(MaterialOrderComponent, {
                backdrop: 'static',
                class: ModalClass.MODAL_LARGE,
                initialState: {
                    model: new MaterialOrderModel(notification, materials),
                },
                ariaLabelledBy: 'material-order-modal-title',
            });
        });
    }

    /**
     * The material pickup dialog. Its result is written back through the material service, which
     * needs the whole list the picked material sits in — so the caller passes it along.
     *
     * @param notification the notification the material belongs to
     * @param material the material being picked up
     * @param materials every ordered material of the notification, in the order the screen shows them
     * @param isNotReceivedSupported whether the dialog offers the "not received" option
     */
    public openMaterialPickup(
        notification: NotificationModel,
        material: MaterialModel,
        materials: MaterialModel[],
        isNotReceivedSupported: boolean,
    ): void {
        const dopModel = notification.dopSet.find(
            (dop: DopModel) =>
                dop.poNum.value === parseInt(material.batchNumber.value, 10) ||
                dop.poNum.value === parseInt(material.awbDelivery, 10),
        );
        const materialList = new ArrayModel<MaterialModel>('MaterialList', materials);

        const modalInstance = this.modalService.show(MaterialPickupComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: {
                id: notification.identifier,
                material,
                dopModel,
                displayNotReceived: isNotReceivedSupported,
            },
            ariaLabelledBy: 'material-pickup-modal-title',
        });

        modalInstance.content?.modalResult
            .pipe(
                mergeMap((result: IMaterialPickupResult) => {
                    if (!result?.materialModel || !result.pickedQuantity) {
                        return of(undefined);
                    }
                    const index = materialList.value.findIndex(
                        (entry) =>
                            MaterialIdentifierModel.compare(entry.materialIdentifier, material.materialIdentifier) &&
                            entry.materialKey === material.materialKey,
                    );
                    return this.materialService.setStatus(
                        materialList,
                        index,
                        result.pickedQuantity,
                        result.statusCode,
                    );
                }),
                tap(() => {
                    if (isNotReceivedSupported) {
                        this.eventService.triggerMaterialConsumptionOrNotReceivedChanged(materialList.value);
                    }
                }),
                catchError((error) => {
                    this.log.error(
                        'NotificationDetailsModalsService: openMaterialPickup: materialService.setStatus failed: ',
                        error,
                    );
                    return of(undefined);
                }),
            )
            .subscribe();
    }

    /**
     * DocBox is not a dialog but a store-driven navigation, and it needs the main equipment the
     * documents are looked up by.
     *
     * @param notification the notification whose documents are opened
     */
    public openDocBox(notification: NotificationModel): void {
        const mainEquipment = notification.getMainEquipment();
        if (!mainEquipment) {
            return;
        }
        this.store.dispatch(
            getDocBox({
                notificationId: notification.getNotificationState().identifier,
                materialNumber: mainEquipment.matnr.value,
                serialNumber: mainEquipment.sernr.value,
            }),
        );
    }

    private addPchReminder(triggerTime: moment.Moment, notificationId: NotificationIdentifier): void {
        this.pchAlertService.addReminder(new Date(triggerTime.toISOString()), notificationId).pipe(take(1)).subscribe();
    }
}
